import { crypto } from '@binance-chain/javascript-sdk'
import React, { Component, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import styled from 'styled-components'

import Infinito from '../../utils/integrations/infinitoBridge'
import { Consumer as BNCClientConsumer } from '../context/BNCClientProvider'
import Modal from '../../components/common/Modal'

const ModalBody = styled.div`
  color: inherit;
  text-align: center;
`

/**
 * The Infinito signing delegate.
 * @param  {preSignCb}  function
 * @param  {postSignCb} function
 * @param  {errCb} function
 * @return {function}
 */
export const InfinitoSigningDelegate = (preSignCb, postSignCb, errCb, ...emulateArgs) => async (
  tx,
  signMsg
) => {
  const signBytes = tx.getSignBytes(signMsg)
  const signObject = JSON.parse(signBytes.toString())
  preSignCb && preSignCb(signBytes, signObject)
  let pubKeyHex
  let sigHex
  const infinito = Infinito.getInstance(...emulateArgs)
  if (!infinito.hasIntegration()) {
    errCb && errCb(new Error('Infinito was not found!'))
    return tx
  }
  try {
    const { publicKey } = await infinito.getPublicKey()
    const { signature } = await infinito.signTransaction(signObject)
    pubKeyHex = publicKey
    sigHex = signature
    postSignCb && postSignCb(publicKey, signature)
  } catch (err) {
    console.error('Infinito error!', err)
    errCb && errCb(err)
    return tx
  }
  if (pubKeyHex && sigHex) {
    const pubKey = crypto.getPublicKey(pubKeyHex)
    return tx.addSignature(pubKey, Buffer.from(sigHex, 'hex'))
  }
  errCb && errCb(new Error('Infinito responded without a pubkey or signature'))
  return tx
}

const InfinitoDelegateConfigurator = ({
  bncClient,
  preSignCb,
  postSignCb,
  errCb,
  isInfinito,
  isInfinitoEmulate,
}) => {
  // similar to componentDidMount and componentDidUpdate:
  useEffect(() => {
    if (!bncClient || !isInfinito) return
    bncClient.setSigningDelegate(
      InfinitoSigningDelegate(preSignCb, postSignCb, errCb, isInfinitoEmulate)
    )
  })
  return null
}

InfinitoDelegateConfigurator.propTypes = {
  bncClient: PropTypes.object.isRequired,
  preSignCb: PropTypes.func,
  postSignCb: PropTypes.func,
  errCb: PropTypes.func,
  isInfinito: PropTypes.bool,
  isInfinitoEmulate: PropTypes.bool,
}

InfinitoDelegateConfigurator.defaultProps = {
  preSignCb: () => {},
  postSignCb: () => {},
  errCb: () => {},
  isInfinito: false,
  isInfinitoEmulate: false,
}

class InfinitoModalHost extends Component {
  static propTypes = {
    isInfinito: PropTypes.bool,
    isInfinitoEmulate: PropTypes.bool,
  }

  static defaultProps = {
    isInfinito: false,
    isInfinitoEmulate: false,
  }

  state = { showModal: false }

  _showSignModal = () => {
    this.setState({ showModal: true })
  }

  _hideSignModal = () => {
    this.setState({ showModal: false })
  }

  _showError = err => {
    // TODO: For now Infinito will return a standard Error object if the user rejects the sign.
    //       We can practically ignore this
    this.setState({ showModal: false }, () => {
      err && alert(err && err.message)
    })
  }

  render() {
    const { showModal } = this.state
    const { isInfinito, isInfinitoEmulate } = this.props
    return (
      <>
        <BNCClientConsumer>
          {bncClient => (
            <InfinitoDelegateConfigurator
              bncClient={bncClient}
              preSignCb={this._showSignModal}
              postSignCb={this._hideSignModal}
              errCb={this._showError}
              isInfinito={isInfinito}
              isInfinitoEmulate={isInfinitoEmulate}
            />
          )}
        </BNCClientConsumer>
        {showModal ? (
          <Modal title="Ready to Confirm">
            <ModalBody>Please confirm the transaction in Infinito Wallet.</ModalBody>
          </Modal>
        ) : null}
      </>
    )
  }
}

const mapStateToProps = ({
  account: {
    flags: { isInfinito, isInfinitoEmulate },
  },
}) => ({
  isInfinito,
  isInfinitoEmulate,
})

export default connect(mapStateToProps)(InfinitoModalHost)
